import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { enquiryApi } from "./api";
import { getEnquiryCookie, removeEnquiryCookie, setEnquiryCookie } from "./cookie";
import { Enquiry } from "./types";
import { getBrowserIanaTimeZoneId } from "@schema-types/enquiryEvaluationTimeZone";

interface UseEnquiryResult {
  enquiry: Enquiry | null;
  isLoading: boolean;
  error: string;
  createEnquiry: (turnstileToken?: string) => Promise<Enquiry>;
}

export const useEnquiry = (journeyId: string, version: string): UseEnquiryResult => {
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>(null);
  const isCreatingRef = useRef(false);

  const loadExistingEnquiry = async (cookie: { enquiryId: string }) => {
    try {
      const existingEnquiry = await enquiryApi.get(cookie.enquiryId);
      setEnquiry(existingEnquiry);
      return existingEnquiry;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        removeEnquiryCookie();
        setEnquiry(null);
        return;
      }
      throw err;
    }
  };

  const createEnquiry = async (turnstileToken?: string): Promise<Enquiry> => {
    if (isCreatingRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (enquiry) return enquiry;
      throw new Error("Enquiry creation is in progress");
    }

    if (enquiry) {
      return enquiry;
    }

    if (!journeyId || !version) {
      throw new Error("Journey ID and version are required to create an enquiry");
    }

    try {
      isCreatingRef.current = true;
      setIsLoading(true);

      const response = await enquiryApi.start({
        journeyId,
        version,
        turnstileToken,
        clientTimeZoneId: getBrowserIanaTimeZoneId(),
      });

      setEnquiryCookie({
        enquiryId: response.enquiryId,
        journeyId,
        version,
      });

      const newEnquiry = await enquiryApi.get(response.enquiryId);
      setEnquiry(newEnquiry);
      return newEnquiry;
    } finally {
      isCreatingRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeJourney = async () => {
      if (!journeyId || !version) {
        setEnquiry(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const cookie = getEnquiryCookie();

        if (cookie && cookie.journeyId === journeyId && cookie.version === version) {
          await loadExistingEnquiry(cookie);
        } else {
          setEnquiry(null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    initializeJourney();
  }, [journeyId, version]);

  return {
    enquiry,
    isLoading,
    error,
    createEnquiry,
  };
};
